import { api } from './client';

export type MailingSpace = 'affilmaster' | 'affilplatform';
export type CampaignStatus = 'draft' | 'scheduled' | 'running' | 'paused' | 'sent' | 'completed' | 'archived';

export interface MailingDashboard {
  services: { key: string; status: string; lastRunAt: string | null; lastSuccessAt: string | null; counter: number; lastError: string | null }[];
  queue: { pending: number; processing: number; total: number; failed: number; skipped: number; sent: number };
  stats: { sent30d: number; sent7d: number; errors30d: number; errors7d: number };
  activeCampaigns: number;
  unsubscribed: number;
  sendgridConfigured: boolean;
  sendLive: boolean;
  shortHost: string | null;
}

export interface CampaignListItem {
  id: number;
  name: string;
  type: string;
  status: CampaignStatus;
  channels: ['mail'];
  country: string | null;
  segmentId: number | null;
  segmentIds: number[];
  segmentName: string | null;
  segmentColor: string | null;
  scheduledAt: string | null;
  sentAt: string | null;
  recipientsEstimate: number | null;
  recipients: number;
  jobsDone: number;
  deliveredTotal: number;
  opensUnique: number;
  clicksTotal: number;
  uniqueClickers: number;
  ctr: number | null;
  templateName: string | null;
  createdAt: string;
}

export interface CampaignClick {
  id: number;
  occurredAt: string | null;
  email: string | null;
  name: string | null;
  url: string | null;
  parentName: string | null;
  source: string | null;
}

export interface CampaignDetail extends CampaignListItem {
  description: string | null;
  subject: string | null;
  senderId: number | null;
  templateId: number | null;
  htmlBody: string | null;
  bouncesTotal: number;
  unsubscribesTotal: number;
  spamReportsTotal: number;
  opensTotal: number;
  jobStates: { pending: number; sending: number; sent: number; failed: number; skipped: number };
  meta: Record<string, unknown> | null;
  createdBy: string | null;
}

export interface CampaignJob {
  id: number;
  recipientAddress: string;
  recipientName: string | null;
  state: string;
  attempts: number;
  lastError: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  openCount: number;
  clickCount: number;
  externalId: string | null;
}

export interface AudienceSegment {
  id: number;
  name: string;
  color: string;
  country: string;
  count: number;
  isActive: boolean;
}

export interface Sender {
  id: number;
  country: string | null;
  name: string;
  email: string;
  replyTo: string | null;
  isDefault: boolean;
  verified: boolean;
  active: boolean;
  notes: string | null;
}

export interface SenderBody {
  country: string | null;
  name: string;
  email: string;
  replyTo: string | null;
  isDefault: boolean;
  active: boolean;
  notes: string | null;
}

export interface CampaignTemplate {
  id: number;
  name: string;
  lang: string;
  country: string | null;
  subject: string | null;
  preheader: string | null;
  bodyHtml: string | null;
  bodyPlain: string | null;
  enabled: boolean;
  isDefault: boolean;
  authorName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export type CampaignTemplateBody = Partial<Omit<CampaignTemplate, 'id' | 'authorName' | 'createdAt' | 'updatedAt' | 'isDefault'>>;

export interface MailingOverview {
  countries: { code: string; contacts: number; subscribed: number; unsubscribed: number }[];
  totals: { contacts: number; unsubscribed: number };
  performance: { campaigns: number; delivered: number; opens: number; clickers: number; openRate: number | null; clickRate: number | null };
}

export interface ContactMailStatus {
  status: 'bounced' | 'inactive';
  reason: string | null;
  since: string | null;
  campaignId: number | null;
}

export interface SubscriptionState {
  email: string;
  subscribed: boolean;
  unsubscribedAt: string | null;
  source: string | null;
  status?: ContactMailStatus | null;
  segments?: Array<{ name: string; color: string | null }>;
}

export interface CampaignBody {
  name?: string;
  segmentIds?: number[];
  senderId?: number | null;
  templateId?: number | null;
  subject?: string | null;
  htmlBody?: string | null;
  scheduledAt?: string | null;
  status?: CampaignStatus;
  meta?: Record<string, unknown> | null;
}

export function mailingApi(space: MailingSpace) {
  const base = `/api/v1/${space}/mailing`;
  return {
    dashboard: () => api.get<MailingDashboard>(`${base}/dashboard`),
    overview: () => api.get<MailingOverview>(`${base}/overview`),
    subscriptions: (emails: string[]) => api.get<SubscriptionState[]>(`${base}/subscriptions?emails=${encodeURIComponent(emails.join(','))}`),
    clearContactStatus: (email: string) => api.post<{ cleared: boolean }>(`${base}/contacts/status/clear`, { email }),
    campaigns: () => api.get<CampaignListItem[]>(`${base}/campaigns`),
    campaign: (id: number) => api.get<CampaignDetail>(`${base}/campaigns/${id}`),
    uploadAsset: (body: { contentBase64: string; mime: string }) => api.post<{ url: string }>(`${base}/assets`, body),
    campaignClicks: (id: number) => api.get<CampaignClick[]>(`${base}/campaigns/${id}/clicks`),
    campaignJobs: (id: number, limit?: number) => api.get<CampaignJob[]>(`${base}/campaigns/${id}/jobs${limit ? `?limit=${limit}` : ''}`),
    createCampaign: (body: CampaignBody) => api.post<{ id: number }>(`${base}/campaigns`, body),
    updateCampaign: (id: number, body: CampaignBody) => api.patch<{ updated: boolean }>(`${base}/campaigns/${id}`, body),
    duplicateCampaign: (id: number) => api.post<{ id: number }>(`${base}/campaigns/${id}/duplicate`),
    archiveCampaign: (id: number) => api.del<{ archived: boolean }>(`${base}/campaigns/${id}`),
    testSend: (body: { to: string; subject: string; html: string; senderId: number }) =>
      api.post<{ ok: boolean; to: string; vocative: string | null }>(`${base}/campaigns/test-send`, body),
    segments: (country?: string) => api.get<AudienceSegment[]>(`${base}/audience/segments${country ? `?country=${country}` : ''}`),
    segmentEstimate: (ids: number[]) => api.get<{ count: number; unsubscribed: number; missingVocative: number }>(`${base}/audience/estimate?segments=${ids.join(',')}`),
    senders: () => api.get<Sender[]>(`${base}/senders`),
    createSender: (body: SenderBody) => api.post<{ id: number }>(`${base}/senders`, body),
    updateSender: (id: number, body: SenderBody) => api.put<{ id: number }>(`${base}/senders/${id}`, body),
    templateCounts: () => api.get<{ active: number; all: number }>(`${base}/templates/counts`),
    templates: (p: { country?: string; all?: boolean; q?: string } = {}) => {
      const qs = new URLSearchParams();
      if (p.country) qs.set('country', p.country);
      if (p.all) qs.set('all', '1');
      if (p.q) qs.set('q', p.q);
      const s = qs.toString();
      return api.get<CampaignTemplate[]>(`${base}/templates${s ? `?${s}` : ''}`);
    },
    template: (id: number) => api.get<CampaignTemplate>(`${base}/templates/${id}`),
    createTemplate: (body: CampaignTemplateBody) => api.post<{ id: number }>(`${base}/templates`, body),
    updateTemplate: (id: number, body: CampaignTemplateBody) => api.put<{ updated: boolean }>(`${base}/templates/${id}`, body),
    archiveTemplate: (id: number) => api.del<{ updated: boolean }>(`${base}/templates/${id}`),
    restoreTemplate: (id: number) => api.put<{ updated: boolean }>(`${base}/templates/${id}`, { enabled: true }),
    duplicateTemplate: (id: number) => api.post<{ id: number }>(`${base}/templates/${id}/duplicate`),
  };
}

export type MailingApi = ReturnType<typeof mailingApi>;
